import { CSS_PX_PER_INCH, MM_PER_INCH } from './pdfScaleUtils';

/**
 * Pen weights for an elevation drawn in CAPTURE mode (`RenderWallElevation`
 * with `isPrimary` and a `previewScale`) — every PDF elevation, the
 * `/render/elevation` page, and the wall-isolation views that borrow the print
 * look.
 *
 * Defined in millimetres ON PAPER, because that is what a drafting weight is.
 * A captured elevation is placed at exactly CSS_PX_PER_INCH logical px per
 * inch (see pdfScaleUtils), so a logical-px stroke converts once, here. The
 * old fixed 4 px printed at 1.06 mm on every line — about four times a normal
 * object line — and filled small cabinets in solid.
 *
 * Schematics draw their own detail as fractions of the stroke they are handed
 * (`strokeWidth * 0.5` …), so one object weight still leaves a hierarchy.
 *
 * See claude-docs/2026-09-17-elevation-tiered-dimensions.md §7.
 */
export const PEN_WEIGHTS_MM = {
  wall: 0.5,
  object: 0.35,
  detail: 0.18, // door panels, pulls, toe kicks — lines INSIDE an object
  annotation: 0.13, // dimension lines, leaders
};

/**
 * Detail as a FRACTION of the object weight, so a schematic can lighten its
 * inner lines without knowing whether it is drawing for screen or for print —
 * it is handed one stroke and scales it. Several schematics already do this by
 * hand with `strokeWidth * 0.5`; this is that convention, derived from the pen
 * table instead of repeated as a literal.
 */
export const SCHEMATIC_DETAIL_RATIO =
  PEN_WEIGHTS_MM.detail / PEN_WEIGHTS_MM.object;

/** Logical px that print at `mm` when placed at CSS_PX_PER_INCH per inch. */
export const penWeightPx = (mm) => (mm / MM_PER_INCH) * CSS_PX_PER_INCH;

export const CAPTURE_STROKE_PX = {
  wall: penWeightPx(PEN_WEIGHTS_MM.wall),
  object: penWeightPx(PEN_WEIGHTS_MM.object),
  detail: penWeightPx(PEN_WEIGHTS_MM.detail),
  annotation: penWeightPx(PEN_WEIGHTS_MM.annotation),
};

// A selected or highlighted object keeps the emphasis it always had (6 px
// against 4 px).
export const EMPHASIS_STROKE_FACTOR = 1.5;
